import type { ImageLoaderProps } from 'next/image';
import { Env } from '@/consts/Env';

const CFIMG_BUCKET_BY_ENV: Record<typeof Env.NEXT_PUBLIC_ENVIRONMENT, string> = {
  staging: 'sweepking-dev-bucket',
  production: 'sweepking-prod-dev-bucket',
};

const CFIMG_ALLOWED_S3_BUCKETS = new Set([
  'sweepking-ecs-dev-dev-bucket',
  'sweepking-dev-bucket',
  'sweepking-prod-dev-bucket',
  'fams-content-stg-sweepking',
]);

const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value);
const S3_VIRTUAL_HOST_PATTERN = /^(.+)\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i;

const resolveBucketImage = (src: string) => {
  try {
    const url = new URL(src);
    const bucket = url.hostname.match(S3_VIRTUAL_HOST_PATTERN)?.[1];
    const key = url.pathname.replace(/^\/+/, '');

    if (!bucket || !key || !CFIMG_ALLOWED_S3_BUCKETS.has(bucket)) {
      return null;
    }

    return { bucket, key };
  } catch {
    return null;
  }
};

const getDefaultBucketImage = (src: string) => ({
  bucket: CFIMG_BUCKET_BY_ENV[Env.NEXT_PUBLIC_ENVIRONMENT],
  key: src,
});

const buildCloudflareImageUrl = (
  image: ReturnType<typeof getDefaultBucketImage>,
  params?: string[],
) => {
  const url = `${Env.NEXT_PUBLIC_API_URL}/cfimg/${image.bucket}/${image.key}`;

  if (!params?.length) {
    return url;
  }

  return `${url}?${params.join('&')}`;
};

const buildCloudflareMediaUrl = (src: string) => {
  if (src.startsWith('/')) {
    return src;
  }

  const bucketImage = isAbsoluteUrl(src)
    ? resolveBucketImage(src)
    : getDefaultBucketImage(src);

  return bucketImage ? buildCloudflareImageUrl(bucketImage) : src;
};

const buildLocalImageUrl = (src: string, width: number, quality: number | undefined) => {
  const params = new URLSearchParams({ w: String(width), q: String(quality || 75) });
  const separator = src.includes('?') ? '&' : '?';

  return `${src}${separator}${params.toString()}`;
};

type FitMode = 'cover' | 'contain';

export interface CloudflareLoaderOptions {
  /** fit=cover crops the image to the given proportions; for game covers */
  fit?: FitMode;
  /** Target width for scaling height with fit=cover (e.g. for 2x) */
  width?: number;
  /** Target height — passed to CDN together with width for fit=cover */
  height?: number;
  /** Return the bucket URL without Cloudflare transform query params. */
  withoutTransformParams?: boolean;
}

const cloudflareLoader = ({ src, width, quality }: ImageLoaderProps) => {
  // 1) Local/static resources served by Next from /public or /_next should not go through cfimg.
  //    Keep width in the returned URL because Next expects custom loaders to use it.
  if (src.startsWith('/')) {
    return buildLocalImageUrl(src, width, quality);
  }

  // 2) Trusted S3 URLs can arrive signed via media.path. Strip the token query
  //    and route the object key through cfimg, same as media.key.
  const bucketImage = isAbsoluteUrl(src)
    ? resolveBucketImage(src)
    : getDefaultBucketImage(src);

  if (!bucketImage) {
    return src;
  }

  const params = [`w=${width}`, `q=${quality || 75}`, 'fit=contain', 'format=auto'];
  return buildCloudflareImageUrl(bucketImage, params);
};

/**
 * Factory for image loader with fit=cover and width+height.
 * Use for game covers where card proportions are fixed (e.g. 3:4).
 */
function createCloudflareLoader(options?: CloudflareLoaderOptions) {
  const {
    fit = 'contain',
    width: baseWidth,
    height: baseHeight,
    withoutTransformParams = false,
  } = options ?? {};

  return ({ src, width, quality }: ImageLoaderProps) => {
    if (src.startsWith('/')) {
      return buildLocalImageUrl(src, width, quality);
    }
    const bucketImage = isAbsoluteUrl(src)
      ? resolveBucketImage(src)
      : getDefaultBucketImage(src);

    if (!bucketImage) {
      return src;
    }

    if (withoutTransformParams) {
      return buildCloudflareImageUrl(bucketImage);
    }

    const params: string[] = [`w=${width}`, `q=${quality || 80}`, `fit=${fit}`, 'format=auto'];
    if (fit === 'cover' && baseWidth != null && baseHeight != null) {
      const h = Math.round((width / baseWidth) * baseHeight);
      params.push(`h=${h}`);
    }

    return buildCloudflareImageUrl(bucketImage, params);
  };
}

export default cloudflareLoader;
export { buildCloudflareMediaUrl, cloudflareLoader, createCloudflareLoader };
